import type {WordMagicCardInfo} from '../types/wordbook';

export interface MagicCardSummary {
    word: string;
    primaryMeaning: string;
    morphology: MorphologyItem[];
    collocations: string[];
    examples: MagicCardExampleItem[];
    similarWords: SimilarWordItem[];
}

export interface MorphologyItem {
    text: string;
    pos: string;
    meaning?: string;
}

export interface MagicCardExampleItem {
    sentence: string;
    translation: string;
}

export interface SimilarWordItem {
    text: string;
    note: string;
    meaning?: string;
}

export function parseMagicCardSummary(json: string | null | undefined): MagicCardSummary | null {
    if (!json) return null;
    try {
        const parsed = JSON.parse(json);
        return {
            word: parsed.word ?? '',
            primaryMeaning: parsed.primaryMeaning ?? '',
            morphology: Array.isArray(parsed.morphology) ? parsed.morphology : [],
            collocations: Array.isArray(parsed.collocations) ? parsed.collocations : [],
            examples: Array.isArray(parsed.examples) ? parsed.examples : [],
            similarWords: Array.isArray(parsed.similarWords) ? parsed.similarWords : [],
        };
    } catch {
        return null;
    }
}

export type MagicCardDisplayStatus =
    | 'READY'
    | 'GENERATING'
    | 'QUEUED'
    | 'FAILED'
    | 'CACHE_ONLY'
    | 'NONE';

export interface MagicCardDisplay {
    status: MagicCardDisplayStatus;
    caption: string | null;
}

export function deriveMagicCardDisplayStatus(
    info: WordMagicCardInfo | undefined,
): MagicCardDisplay {
    if (!info) return {status: 'NONE', caption: null};
    if (info.userStatus === 'READY' && info.cacheExists) {
        return {
            status: 'READY',
            caption: `${info.cacheProvider ?? '—'} · ${info.cacheModelName ?? '—'} · v${info.cacheVersion ?? '?'}`,
        };
    }
    if (info.userStatus === 'GENERATING') return {status: 'GENERATING', caption: null};
    if (info.userStatus === 'QUEUED') return {status: 'QUEUED', caption: null};
    if (info.userStatus === 'FAILED') return {status: 'FAILED', caption: null};
    if (!info.userStatus && info.cacheExists) {
        return {
            status: 'CACHE_ONLY',
            caption: `${info.cacheProvider ?? '—'} · v${info.cacheVersion ?? '?'}`,
        };
    }
    return {status: 'NONE', caption: null};
}

export interface MagicCardCounts {
    ready: number;
    generating: number;
    queued: number;
    failed: number;
    cacheOnly: number;
    none: number;
    total: number;
}

export function countMagicCardStatuses(
    words: Array<{magicCard?: WordMagicCardInfo; deletedAt: string | null}>,
): MagicCardCounts {
    const counts: MagicCardCounts = {
        ready: 0,
        generating: 0,
        queued: 0,
        failed: 0,
        cacheOnly: 0,
        none: 0,
        total: 0,
    };
    for (const w of words) {
        if (w.deletedAt) continue;
        counts.total++;
        const {status} = deriveMagicCardDisplayStatus(w.magicCard);
        switch (status) {
            case 'READY':
                counts.ready++;
                break;
            case 'GENERATING':
                counts.generating++;
                break;
            case 'QUEUED':
                counts.queued++;
                break;
            case 'FAILED':
                counts.failed++;
                break;
            case 'CACHE_ONLY':
                counts.cacheOnly++;
                break;
            case 'NONE':
                counts.none++;
                break;
        }
    }
    return counts;
}
